import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Crown, Users, House, PencilSimple, Trash, Warning, PaperPlaneTilt, WhatsappLogo, EnvelopeSimple, Copy, Check, LinkSimple } from "@phosphor-icons/react";

const ROLES = ["coach", "head_coach", "parent", "external_coach"];

const ROLE_LABEL = {
  head_coach: "Admins",
  coach: "Coaches",
  parent: "Parents",
  external_coach: "External coaches",
};

const ROLE_ICON = {
  head_coach: Crown,
  coach: Users,
  parent: House,
  external_coach: Users,
};

const ROLE_ORDER = ["head_coach", "coach", "parent", "external_coach"];

export default function ClubUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [staffInvites, setStaffInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteStaffOpen, setInviteStaffOpen] = useState(false);
  const [shareInvite, setShareInvite] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "coach", phone: "" });
  const [err, setErr] = useState("");

  const refresh = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/invites/staff").then((r) => setStaffInvites(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const grouped = useMemo(() => {
    const g = { head_coach: [], coach: [], parent: [], external_coach: [] };
    (users || []).forEach((u) => {
      const key = ROLE_ORDER.includes(u.role) ? u.role : "coach";
      g[key].push(u);
    });
    return g;
  }, [users]);

  const sendInviteToUser = async (u) => {
    try {
      const res = await api.post(`/users/${u.id}/invite`);
      setShareInvite(res.data);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="club-users">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label">Club</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight">Users</h1>
        </div>
        <button className="btn-primary" onClick={() => setInviteStaffOpen(true)} data-testid="invite-user-btn">
          <PaperPlaneTilt size={16} weight="bold" /> Invite
        </button>
      </div>

      {staffInvites.filter((i) => i.status === "pending").length > 0 && (
        <section data-testid="pending-staff-invites">
          <div className="font-label mb-2">Pending staff invites</div>
          <div className="grid gap-2">
            {staffInvites.filter((i) => i.status === "pending").map((inv) => (
              <div key={inv.id} className="surface p-4 flex items-center justify-between gap-2" data-testid={`staff-invite-${inv.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{inv.parent_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{inv.parent_email}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="role-badge">{roleLabel({ role: inv.role, title: inv.title })}</span>
                  <button onClick={() => setShareInvite(inv)} className="btn-ghost text-xs" data-testid={`share-staff-invite-${inv.id}`} title="Share invite link">
                    <PaperPlaneTilt size={14} weight="bold" /> Share
                  </button>
                  <button
                    onClick={async () => { await api.delete(`/invites/${inv.id}`); refresh(); }}
                    className="btn-ghost text-[#ff0026] text-xs"
                    data-testid={`revoke-staff-invite-${inv.id}`}
                    title="Revoke invite"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!users && <div className="skeleton h-32" />}

      {users && users.length === 0 && (
        <div className="surface p-6 text-zinc-400 text-sm">No users yet.</div>
      )}

      {users && ROLE_ORDER.map((role) => {
        const list = grouped[role];
        if (!list || list.length === 0) return null;
        const Icon = ROLE_ICON[role];
        return (
          <section key={role} data-testid={`section-${role}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={16} weight="duotone" className="text-[#dfff00]" />
              <h2 className="font-heading uppercase text-sm tracking-wider">{ROLE_LABEL[role]}</h2>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded" data-testid={`count-${role}`}>
                {list.length}
              </span>
            </div>
            <div className="grid gap-2">
              {list.map((u) => {
                const isInactive = u.active === false;
                return (
                  <div key={u.id} className={`surface p-4 flex items-center justify-between gap-2 ${isInactive ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold truncate flex items-center gap-2">
                        {u.name}
                        {isInactive && <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">disabled</span>}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="role-badge">{roleLabel(u)}</span>
                      {u.role !== "super_admin" && (
                        <>
                          <button
                            onClick={() => sendInviteToUser(u)}
                            className="btn-ghost text-xs"
                            data-testid={`send-invite-user-${u.id}`}
                            title="Send activation invite — they set their own password"
                          >
                            <PaperPlaneTilt size={14} weight="bold" />
                          </button>
                          <button onClick={() => setEditing(u)} className="btn-ghost text-xs" data-testid={`edit-user-${u.id}`} title="Edit user">
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          {u.id !== me?.id && (
                            <button onClick={() => setDeleting(u)} className="btn-ghost text-[#ff0026] text-xs" data-testid={`delete-user-${u.id}`} title="Delete user">
                              <Trash size={14} weight="bold" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {inviteStaffOpen && (
        <InviteStaffModal
          onClose={() => setInviteStaffOpen(false)}
          onCreated={(inv) => {
            setInviteStaffOpen(false);
            setShareInvite(inv);
            refresh();
          }}
        />
      )}

      {shareInvite && <StaffShareModal invite={shareInvite} onClose={() => setShareInvite(null)} />}

      {editing && (
        <EditClubUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {deleting && (
        <DeleteUserModal
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); refresh(); }}
        />
      )}
    </div>
  );
}

function InviteStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "coach", title: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await api.post("/invites/staff", form);
      onCreated(res.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="invite-staff-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading uppercase text-xl">Invite user</h2>
            <p className="text-xs text-zinc-500 mt-0.5">They get a link and set their own password.</p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="font-label block mb-1">Name</label>
            <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-form-name" />
          </div>
          <div>
            <label className="font-label block mb-1">Email</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} data-testid="staff-form-email" />
          </div>
          <div>
            <label className="font-label block mb-1">Phone (optional, for WhatsApp)</label>
            <input className="input" placeholder="+447700900111" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-form-phone" />
          </div>
          <div>
            <label className="font-label block mb-1">Access level</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-form-role">
              <option value="coach">Coach — evaluates assigned players</option>
              <option value="head_coach">Admin — full club access</option>
            </select>
          </div>
          <div>
            <label className="font-label block mb-1">Title (optional)</label>
            <input className="input" placeholder="e.g. Director of Tennis, Academy Manager" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="staff-form-title" />
            <div className="text-[10px] text-zinc-500 mt-1">Shown instead of the role label across the app.</div>
          </div>
          {err && <div className="text-sm text-[#ff0026]" data-testid="staff-form-error">{err}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="staff-form-submit">
            {busy ? "Creating…" : "Create invite link"}
          </button>
          <p className="text-[10px] text-zinc-500 text-center">
            Inviting a parent? Use the <span className="text-zinc-300">Invites</span> tab — parent invites are linked to a player.
          </p>
        </div>
      </form>
    </div>
  );
}

function normalisePhone(raw) {
  if (!raw) return "";
  let s = String(raw).trim().replace(/[^\d+]/g, "");
  if (s.startsWith("+")) return s.slice(1);
  if (s.startsWith("00")) return s.slice(2);
  if (s.startsWith("0")) return "44" + s.slice(1);
  return s;
}

function StaffShareModal({ invite, onClose }) {
  const [copied, setCopied] = useState(false);
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [emailErr, setEmailErr] = useState("");

  const url = `${window.location.origin}/invite/${invite.token}`;
  const inviteRoleLabel = roleLabel({ role: invite.role, title: invite.title });
  const message = `Hi ${invite.parent_name}, you've been invited to join us on courtsideOS as ${inviteRoleLabel}. Activate your account here: ${url}`;
  const phone = normalisePhone(invite.parent_phone);
  const waUrl = phone
    ? `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;

  const sendEmail = async () => {
    if (!invite.parent_email || emailState === "sending") return;
    setEmailState("sending");
    setEmailErr("");
    try {
      await api.post(`/invites/${invite.id}/send-email`, { base_url: window.location.origin });
      setEmailState("sent");
    } catch (e) {
      setEmailState("error");
      setEmailErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="staff-share-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading uppercase text-xl">Share invite</h2>
          <button onClick={onClose} className="btn-ghost" data-testid="staff-share-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          {invite.parent_name} · <span className="text-zinc-300">{inviteRoleLabel}</span>
        </p>

        <div className="bg-[#09090b] border border-[#27272a] rounded-md p-3 flex items-center gap-2 mb-4">
          <LinkSimple size={16} className="text-zinc-500 shrink-0" />
          <span className="text-xs font-mono text-zinc-300 truncate" data-testid="staff-share-url">{url}</span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="surface p-4 flex flex-col items-center gap-2 transition-colors hover:border-[#16ec06]/60 text-[#16ec06]"
            data-testid="staff-share-whatsapp"
          >
            <WhatsappLogo size={28} weight="bold" />
            <span className="text-xs font-bold uppercase tracking-wider">WhatsApp</span>
          </a>
          <button
            type="button"
            onClick={sendEmail}
            disabled={!invite.parent_email || emailState === "sending"}
            className={`surface p-4 flex flex-col items-center gap-2 transition-colors ${invite.parent_email ? "hover:border-[#dfff00]/60 text-[#dfff00]" : "opacity-40 cursor-not-allowed text-zinc-500"}`}
            data-testid="staff-share-email"
            title={invite.parent_email ? `Send to ${invite.parent_email}` : "No email on this invite"}
          >
            {emailState === "sent" ? <Check size={28} weight="bold" className="text-[#16ec06]" /> : <EnvelopeSimple size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">
              {emailState === "sending" ? "Sending…" : emailState === "sent" ? "Sent" : "Email"}
            </span>
          </button>
          <button
            type="button"
            onClick={copy}
            className="surface p-4 flex flex-col items-center gap-2 transition-colors hover:border-white/40 text-zinc-200"
            data-testid="staff-share-copy"
          >
            {copied ? <Check size={28} weight="bold" className="text-[#16ec06]" /> : <Copy size={28} weight="bold" />}
            <span className="text-xs font-bold uppercase tracking-wider">{copied ? "Copied" : "Copy"}</span>
          </button>
        </div>

        {emailState === "error" && (
          <div className="text-xs text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-2 mt-3" data-testid="staff-share-email-error">
            {emailErr}
          </div>
        )}

        <p className="text-xs text-zinc-500 mt-4">
          They open the link, set their own password, and land in the app with {invite.role === "head_coach" ? "full club-admin access" : "coach access"}.
        </p>
      </div>
    </div>
  );
}

function EditClubUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    title: user.title || "",
    active: user.active !== false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {};
      if (form.name !== user.name) payload.name = form.name;
      if (form.email.toLowerCase() !== (user.email || "").toLowerCase()) payload.email = form.email;
      if ((form.phone || "") !== (user.phone || "")) payload.phone = form.phone;
      if ((form.title || "") !== (user.title || "")) payload.title = form.title;
      if (form.active !== (user.active !== false)) payload.active = form.active;
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await api.patch(`/users/${user.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="club-edit-user-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-label">Edit {roleLabel(user.role)}</div>
            <h2 className="font-heading uppercase text-xl">{user.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="font-label block mb-1">Name</label>
            <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="club-edit-name" />
          </div>
          <div>
            <label className="font-label block mb-1">Email</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.replace(/\s+/g, "") })} data-testid="club-edit-email" />
          </div>
          <div>
            <label className="font-label block mb-1">Phone</label>
            <input className="input" placeholder="+447700900111" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="club-edit-phone" />
          </div>
          <div>
            <label className="font-label block mb-1">Title (optional)</label>
            <input className="input" placeholder="e.g. Performance Coach" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="club-edit-title" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="club-edit-active" />
            <span className="text-sm">Active (uncheck to disable login)</span>
          </label>
          {err && <div className="text-sm text-[#ff0026]" data-testid="club-edit-error">{err}</div>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="club-edit-submit">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function DeleteUserModal({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const del = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/users/${user.id}`);
      onDeleted();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const consequence =
    user.role === "parent"
      ? "Their link to players will be removed — the players' evaluation history stays intact."
      : "Their active player assignments will be ended — the evaluations they wrote stay intact.";

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="delete-user-modal">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-[#ff0026]/10 flex items-center justify-center shrink-0">
            <Warning size={20} weight="bold" className="text-[#ff0026]" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading uppercase text-xl">Delete {user.name}?</h2>
            <p className="text-sm text-zinc-400 mt-1">
              This permanently removes their {roleLabel(user.role)} account ({user.email}). {consequence}
            </p>
          </div>
        </div>
        {err && <div className="text-sm text-[#ff0026] mb-3" data-testid="delete-user-error">{err}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={busy}>Cancel</button>
          <button
            onClick={del}
            disabled={busy}
            className="flex-1 h-10 rounded-md font-bold text-xs uppercase tracking-wider bg-[#ff0026] text-white hover:bg-[#d60020] disabled:opacity-50"
            data-testid="confirm-delete-user"
          >
            {busy ? "Deleting…" : "Delete user"}
          </button>
        </div>
      </div>
    </div>
  );
}
